import {
    BodySurvey,
    QuestionHistorical,
    SurveyHistorical,
} from '../shared/types/survey.interface';
import { apiService } from './index';
import {
    IRegisterAnswer,
    IRegisterQuestions,
    IRegisterSurvey,
    IRegisterSurveyResponse,
} from './interfaces/survey';

export const asyncGetSurveys = async (
    page?: number,
    pageSize?: number,
    projectName?: string,
    startDate?: string,
    endDate?: string,
    responseStartDate?: string,
    responseEndDate?: string
): Promise<BodySurvey> => {
    return await apiService.get('/api/survey', {
        limit: pageSize || '',
        page: page || '',
        projectName: projectName || '',
        startDate: startDate || '',
        endDate: endDate || '',
        responseStartDate: responseStartDate || '',
        responseEndDate: responseEndDate || '',
    });
};

export const asyncPostSurvey = async (formSurvey: IRegisterSurvey) => {
    return await apiService.post('/api/survey', formSurvey);
};

export const asyncGetSurvey = async (
    id: string | undefined,
    active?: boolean | undefined
) => {
    return await apiService.get(
        `/api/survey/${id}`,
        active ? { active } : undefined
    );
};

export const asyncPatchSurvey = async (id: string, formSurvey: any) => {
    return await apiService.put(`/api/survey/${id}`, formSurvey);
};

export const asyncDeleteSurvey = async (id: string) => {
    return await apiService.delete(`/api/survey/${id}`);
};

export const asyncGetQuestions = async (
    page?: number,
    pageSize?: number,
    survey?: string,
    showAnswers?: boolean
) => {
    return await apiService.get('/api/question', {
        limit: pageSize || '',
        page: page || '',
        survey: survey || '',
        showAnswers: showAnswers || '',
    });
};

export const asyncPostQuestion = async (formQuestions: IRegisterQuestions) => {
    return await apiService.post('/api/question', formQuestions);
};

export const asyncUpdatePositions = async (survey: string, items: string[]) => {
    return await apiService.put('/api/question', { survey, items });
};

export const asyncUpdatePositionsAnwers = async (
    question: string,
    items: string[]
) => {
    return await apiService.put('/api/answer', { question, items });
};

export const asyncDeleteQuestion = async (id: string, idSurvey: string) => {
    return await apiService.delete(`/api/question/${id}/${idSurvey}`);
};

export const asyncGetQuestion = async (id: string | undefined) => {
    return await apiService.get(`/api/question/${id}`);
};

export const asyncPatchQuestion = async (
    id: string,
    formQuestions: IRegisterQuestions
) => {
    return await apiService.put(`/api/question/${id}`, formQuestions);
};

export const asyncPostAnswer = async (formAnswer: IRegisterAnswer) => {
    return await apiService.post('/api/answer', formAnswer);
};

export const asyncGetAnswersByQuestion = async (
    question: string | undefined,
    page?: number,
    pageSize?: number,
    status?: boolean
) => {
    return await apiService.get('/api/answer', {
        question,
        limit: pageSize || '',
        page: page || '',
        status,
    });
};

export const asyncPatchAnswer = async (
    id: string | undefined,
    formAnswer: IRegisterAnswer
) => {
    return await apiService.put(`/api/answer/${id}`, formAnswer);
};

export const asyncDeleteAnswer = async (
    id: string | undefined,
    idQuestion: string | undefined,
    idSurvey: string | undefined
) => {
    return await apiService.delete(
        `/api/answer/${id}/${idQuestion}/${idSurvey}`
    );
};

export const asyncPostSurveyResponse = async (
    formSurveyResponse: IRegisterSurveyResponse
) => {
    return await apiService.post('/api/survey/response', formSurveyResponse);
};

export const asyncPostValidateReference = async (payload: {
    survey: string;
    question: string;
    value: string;
}): Promise<{
    valid: boolean;
    message?: string;
    nombre?: string;
    email?: string;
}> => {
    const notValidMessage = 'El registro no está dentro de la validación.';

    try {
        const body: any = await apiService.post(
            '/api/survey/validate-reference',
            payload
        );
        if (body?.errors) {
            return { valid: false, message: notValidMessage };
        }
        const items = body?.items;
        if (items?.valid === true) {
            return {
                valid: true,
                nombre: items.nombre,
                email: items.email,
            };
        }
        return {
            valid: false,
            message: items?.message || notValidMessage,
        };
    } catch {
        return { valid: false, message: notValidMessage };
    }
};

export const asyncGetQuestionDependency = async () => {
    return await apiService.get('/api/question/questionDependency');
};

export const asyncPostExportData = async (
    surveyId: string,
    startDate: string,
    endDate: string
) => {
    return await apiService.post(`/api/survey/export/${surveyId}`, {
        startDate: startDate || null,
        endDate: endDate || null,
    });
};

export const asyncGetSurveyHistorical = async (
    surveyId: string
): Promise<SurveyHistorical[]> => {
    const historicals: { items: SurveyHistorical[] } = await apiService.get(
        `/api/survey/historical/${surveyId}`
    );
    return historicals?.items;
};

export const asyncGetQuestionHistorical = async (
    surveyId: string
): Promise<QuestionHistorical[]> => {
    const historicals: { items: QuestionHistorical[] } = await apiService.get(
        `/api/question/historical/${surveyId}`
    );
    return historicals?.items;
};

export const asyncPostAnswerFile = async (formData: FormData) => {
    return await apiService.post('/api/answer/file', formData);
};

export const asyncGetValidationCedula = async (id: string | undefined) => {
    return await apiService.get(`/api/temporal_previsora/${id}`);
};

export const asyncGetTemporalPrevisoraComplete = async (
    document: string
): Promise<any> => {
    return await apiService.get(
        `/api/temporal_previsora/complete/${encodeURIComponent(document)}`
    );
};

export const asyncGetSurveyResponseAnswer = async (
    answerText: string,
    questionId: string,
    startDate?: string,
    endDate?: string
): Promise<any> => {
    let url = `/api/survey/response/answer/${answerText}/${questionId}`;
    if (startDate && endDate) {
        url += `?startDate=${encodeURIComponent(startDate)}&endDate=${encodeURIComponent(endDate)}`;
    }
    const historicals: { items: any } = await apiService.get(url);
    return historicals?.items;
};

export const asyncGetAnswerByParent = async (
    questionId: string,
    parentId: string
): Promise<any> => {
    return await apiService.get(`/api/answer/${questionId}/parent/${parentId}`);
};

export const asyncValidationAnswerList = async (payload: {
    document: string;
    surveyId: string;
    numberlist: number;
    idsAnswers: string[];
}): Promise<string[]> => {
    const response: { items: string[] } = await apiService.post(
        '/api/survey/response/question',
        payload
    );
    return Array.isArray(response?.items) ? response.items : [];
};
